import * as React from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * NewListForm — the "add a list" tail column at the right end of a board.
 * Collapsed by default; expands into an inline input on click.
 */
export default function NewListForm({ companyId, boardId }) {
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  async function submit(e) {
    e.preventDefault();
    const val = name.trim();
    if (!val || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/lists`, { name: val });
      setName("");
      setOpen(false);
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2?.response?.data?.detail) || "Could not add list");
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-[280px] shrink-0 h-11 rounded-md border border-dashed border-brand-rule bg-card/60 hover:bg-card text-[12.5px] text-brand-muted hover:text-brand-violet flex items-center justify-center gap-1.5 transition-colors"
        data-testid="board-add-list"
      >
        <Plus size={13} /> Add a list
      </button>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="w-[280px] shrink-0 rounded-md border border-brand-rule bg-card p-2 space-y-2"
      data-testid="board-new-list-form"
    >
      <input
        autoFocus
        className="field-input"
        placeholder="List name…"
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        data-testid="board-new-list-name"
      />
      <div className="flex gap-2">
        <button type="submit" className="btn-violet flex-1" disabled={busy || !name.trim()}
                data-testid="board-new-list-submit">
          {busy ? "…" : "Add list"}
        </button>
        <button type="button" onClick={() => { setOpen(false); setName(""); }}
                className="btn-ghost h-8 w-8 !p-0" data-testid="board-new-list-cancel"
                title="Cancel">
          <X size={13} />
        </button>
      </div>
    </form>
  );
}
